import {
  GoogleMap,
  useJsApiLoader,
  Autocomplete,
  Polygon,
} from '@react-google-maps/api'
import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  createLatestRequest,
  type LatestRequest,
  type RequestSequence,
} from '@/utils/latestRequest'
import {
  Box,
  Paper,
  Typography,
  Chip,
  Slide,
  Grow,
  CircularProgress,
  IconButton,
} from '@mui/material'
import { Close, DragIndicator, Search } from '@mui/icons-material'
import PolylineIcon from '../../assets/Polyline.svg'
import GroupIcon from '@assets/map_header_icons/Group.svg'
import MarkerInfoCard from './MarkerInfo'
import { usePolygonDraw, verticesToPath } from './usePolygonDraw'
import {
  getClusteredMarkers,
  getClusteredMarkerStatus,
  type ClusterMarker,
  type MapBounds,
} from '@/services/consumerService'
import {
  getAccountMapExtent,
  type GeoBounds,
} from '@/services/dashboardService'
import { SuperClustering, SimpleMarker } from './SuperClustering'
import { ALERT_RED, MARKER_PIN_SIZE, NO_ALERT_BLUE } from './clusterIcon'
import { separateClusters } from './separateClusters'
import { cancelMarkerTiles, fetchMarkersInView } from './markerTileQuery'
import { coverBounds, gridFor, TILE_CELLS, type Grid } from './markerTiles'
import {
  alertStateOf,
  indexStatus,
  mergeStatus,
  NO_STATUS,
  type MapCell,
  type StatusByCell,
} from './markerStatus'
import {
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_OPTIONS,
} from '../../constants/googleMaps'
import { useAppTheme } from '@/theme/ThemeContext'
import {
  INITIAL_BOUNDS_ZOOM,
  cameraFromView,
  decideMapView,
  firstFetchBox,
  type Fetched,
  type LatLng,
  type MapCamera,
  type MapViewUnavailableReason,
} from '@/utils/mapView'
import { debugLog } from '@/utils/debugLog'

const UNAVAILABLE_COPY: Record<MapViewUnavailableReason, string> = {
  'no-site': 'לא נבחר אתר',
  'no-coordinates': 'אין מיקום למד זה',
  'no-markers': 'אין מיקומים להצגה באתר זה',
  'markers-error': 'שגיאה בטעינת המיקומים',
  'extent-error': 'שגיאה בטעינת מיקום האתר',
}

const LOADING = { status: 'loading' } as const

const LOADING_COPY = 'טוען מפה...'

const SELECTED_POLYGON_OPTIONS: google.maps.PolygonOptions = {
  fillColor: '#487CCA',
  fillOpacity: 0.27,
  strokeColor: '#487CCA',
  strokeWeight: 2,
  strokeOpacity: 0.8,
  clickable: false,
}


const applyCamera = (map: google.maps.Map, camera: MapCamera) => {
  map.setCenter(camera.center)
  map.setZoom(camera.zoom)
  if (camera.fitBounds) {
    map.fitBounds(
      new google.maps.LatLngBounds(
        { lat: camera.fitBounds.minLat, lng: camera.fitBounds.minLng },
        { lat: camera.fitBounds.maxLat, lng: camera.fitBounds.maxLng }
      )
    )
  }
}

const containerStyle = {
  width: '100%',
  height: '100%',
}

// Clean map styles without Google branding - LIGHT MODE
const lightMapStyles = [
  {
    featureType: 'poi',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'road',
    stylers: [{ saturation: -50 }],
  },
  {
    featureType: 'water',
    stylers: [{ color: '#e3f2fd' }],
  },
  {
    featureType: 'administrative',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'administrative',
    elementType: 'geometry',
    stylers: [{ visibility: 'off' }],
  },
]

// Dark mode map styles
const darkMapStyles = [
  { elementType: 'geometry', stylers: [{ color: '#1d2c4d' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8ec3b9' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1a3646' }] },
  {
    featureType: 'administrative.country',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#4b6878' }],
  },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#64779e' }],
  },
  {
    featureType: 'administrative.province',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#4b6878' }],
  },
  {
    featureType: 'landscape.man_made',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#334e87' }],
  },
  {
    featureType: 'landscape.natural',
    elementType: 'geometry',
    stylers: [{ color: '#023e58' }],
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [{ color: '#283d6a' }],
  },
  {
    featureType: 'poi',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#6f9ba5' }],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#1d2c4d' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry.fill',
    stylers: [{ color: '#023e58' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#3C7680' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#304a7d' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#98a5be' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#1d2c4d' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#2c6675' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#255763' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#b0d5ce' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#023e58' }],
  },
  {
    featureType: 'transit',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#98a5be' }],
  },
  {
    featureType: 'transit',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#1d2c4d' }],
  },
  {
    featureType: 'transit.line',
    elementType: 'geometry.fill',
    stylers: [{ color: '#283d6a' }],
  },
  {
    featureType: 'transit.station',
    elementType: 'geometry',
    stylers: [{ color: '#3a4762' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#0e1626' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#4e6d70' }],
  },
  {
    featureType: 'administrative',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'administrative',
    elementType: 'geometry',
    stylers: [{ visibility: 'off' }],
  },
]

const baseMapOptions = {
  zoomControl: false,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
  gestureHandling: 'greedy' as const,
  scrollwheel: true,
  disableDoubleClickZoom: false,
  draggable: true,
  keyboardShortcuts: false,
}

// Markers inside the box grown by half its size on each side. A box across
// the 180th meridian (xmin > xmax) keeps every marker.
const withinMargin = (markers: SimpleMarker[], box: MapBounds) => {
  if (box.xmin > box.xmax) return markers
  const dx = (box.xmax - box.xmin) / 2
  const dy = (box.ymax - box.ymin) / 2
  return markers.filter(
    ({ position: { lat, lng } }) =>
      lng >= box.xmin - dx &&
      lng <= box.xmax + dx &&
      lat >= box.ymin - dy &&
      lat <= box.ymax + dy
  )
}

// The map reloads a marker's image whenever it gets a new icon object, so each
// look is built once.
const pinIcons = new Map<string, google.maps.Icon>()

const createCustomMarkerIcon = (isSelected: boolean, marker?: SimpleMarker) => {
  // Check if Google Maps is loaded
  if (typeof google === 'undefined' || !google.maps) {
    return undefined
  }

  const fillColor =
    alertStateOf(marker?.alerts) === 'FULL' ? ALERT_RED : NO_ALERT_BLUE
  const key = `${isSelected}/${fillColor}`
  const cached = pinIcons.get(key)
  if (cached) return cached

  const icon = {
    url: isSelected
      ? 'data:image/svg+xml;charset=UTF-8,' +
      encodeURIComponent(`
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="10" cy="10" r="9" fill="${fillColor}" stroke="#FFFFFF" stroke-width="2"/>
        </svg>
      `)
      : 'data:image/svg+xml;charset=UTF-8,' +
      encodeURIComponent(`
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="8" cy="8" r="7" fill="${fillColor}" stroke="#FFFFFF" stroke-width="2"/>
        </svg>
      `),
    scaledSize: isSelected
      ? new google.maps.Size(20, 20)
      : new google.maps.Size(MARKER_PIN_SIZE, MARKER_PIN_SIZE),
    anchor: isSelected
      ? new google.maps.Point(10, 10)
      : new google.maps.Point(MARKER_PIN_SIZE / 2, MARKER_PIN_SIZE / 2),
  }
  pinIcons.set(key, icon)
  return icon
}

export default function MapContainer({
  fullPage = false,
  interaction = 'interactive',
  showAllMarkers = true,
  coordinates = null,
  singleMarkerData,
  polygon = null,
  onPolygonComplete,
  onPolygonClear,
  onDrawingStart,
  bottomOverlayHeight,
  filters,
}: {
  fullPage?: boolean
  interaction?: 'interactive' | 'view'
  showAllMarkers?: boolean
  coordinates?: { latitude: number; longitude: number } | null
  singleMarkerData?: { id: number; name: string; address: string }
  polygon?: string[] | null
  onPolygonComplete?: (vertices: string[]) => void
  onPolygonClear?: () => void
  onDrawingStart?: () => void
  /** Height of whatever the page lays over the bottom of the screen, read when a polygon is framed. */
  bottomOverlayHeight?: () => number
  filters?: { name: string; value: string[] }[]
}) {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  // Dynamic map options based on theme
  const mapOptions = useMemo(() => ({
    ...baseMapOptions,
    styles: isDarkMode ? darkMapStyles : lightMapStyles,
  }), [isDarkMode])

  // Theme-aware UI colors
  const uiColors = {
    buttonBg: isDarkMode ? '#1D325A' : '#FFFFFF',
    buttonBorder: isDarkMode ? '#374151' : '#EBE9E4',
    buttonBorderActive: isDarkMode ? '#60A5FA' : '#487CCA',
    searchBg: isDarkMode ? '#1D325A' : '#FFFFFF',
    searchBorder: isDarkMode ? '#374151' : '#EBE9E4',
    searchText: isDarkMode ? '#FFFFFF' : '#003F61',
    searchPlaceholder: isDarkMode ? '#94A3B8' : '#003F61',
    iconFilter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
  }

  // @ts-ignore unused but kept for reference
  const canClickMarkers = interaction === 'interactive'
  const canDraw = interaction === 'interactive'
  const initialFitDone = useRef(false)
  const isFirstFetch = useRef(true)
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)
  const [activeMarker, setActiveMarker] = useState<SimpleMarker | null>(null)

  const onAutocompleteLoad = (
    autocomplete: google.maps.places.Autocomplete
  ) => {
    autocompleteRef.current = autocomplete
  }

  const onPlaceChanged = () => {
    if (autocompleteRef.current !== null && map) {
      const place = autocompleteRef.current.getPlace()
      if (place.geometry?.location) {
        map.panTo(place.geometry.location)
        map.setZoom(15)
      }
    }
  }

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    version: '3.62',
    libraries: GOOGLE_MAPS_LIBRARIES,
    ...GOOGLE_MAPS_OPTIONS,
  })

  const [allMarkers, setAllMarkers] = useState<SimpleMarker[]>([])
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [selected, setSelected] = useState<number[]>([])
  const accountId = localStorage.getItem('accountId')
  const [extent, setExtent] = useState<Fetched<GeoBounds | null>>(LOADING)
  const [discovery, setDiscovery] = useState<Fetched<LatLng[]>>(LOADING)
  const appliedCameraRef = useRef<string | null>(null)

  // Depend on the coordinate values, not on the object: callers build the prop
  // inline, so the object identity changes on every render.
  const singleLat = coordinates?.latitude
  const singleLng = coordinates?.longitude

  const view = useMemo(
    () =>
      decideMapView({
        accountId,
        showAllMarkers,
        coordinates:
          singleLat === undefined || singleLng === undefined
            ? null
            : { latitude: singleLat, longitude: singleLng },
        extent,
        markers: discovery,
      }),
    [
      accountId,
      showAllMarkers,
      singleLat,
      singleLng,
      extent,
      discovery,
    ]
  )
  const camera = useMemo(() => cameraFromView(view), [view])

  // The site extent aims the map before the first marker arrives. A null extent
  // or a failed request leaves the map pending until the markers decide it.
  useEffect(() => {
    if (!showAllMarkers || !accountId) return

    let current = true
    setExtent(LOADING)

    getAccountMapExtent(accountId)
      .then(bounds => {
        if (current) setExtent({ status: 'loaded', value: bounds })
      })
      .catch(error => {
        console.error('[MapContainer] Failed to fetch site extent:', error)
        if (current) setExtent({ status: 'failed' })
      })

    return () => {
      current = false
    }
  }, [accountId, showAllMarkers])

  // Transform ClusterMarker from API to SimpleMarker for SuperClustering
  const transformClusterToSimpleMarker = useCallback(
    (cluster: MapCell, index: number): SimpleMarker => ({
      id: cluster.premiseId || -(index + 1), // Negative ID for clusters without premiseId
      name: cluster.payerName || '',
      address: cluster.address || '',
      lastSeen: cluster.lastReadingAt || undefined,
      position: {
        lat: cluster.latitude,
        lng: cluster.longitude,
      },
      // Store pointCount to differentiate clusters from single markers
      pointCount: cluster.pointCount,
      meterNumber: cluster.meterNumber,
      alerts: cluster.alerts,
    }),
    []
  )

  // Track filter changes so loadMarkersInView can use wide bounds when filters change
  const lastFetchedFiltersRef = useRef(filters)

  // A new filter set stops every markers request. A viewport move stops only the older viewport
  // request, so it cannot take the discovery answer away from the camera decision.
  const discoverySequence = useRef<RequestSequence>({ current: 0 })
  const viewportSequence = useRef<RequestSequence>({ current: 0 })
  const discoveryRequest = useRef<LatestRequest | null>(null)
  const viewportRequest = useRef<LatestRequest | null>(null)
  const queryClient = useQueryClient()
  const lastStatus = useRef<StatusByCell>(NO_STATUS)
  // How many meters the site and filter set put on the map, from the last
  // discovery fetch. It picks the grid; unknown counts as many.
  const metersOnMap = useRef(Infinity)
  const shownRequest = useRef<LatestRequest | null>(null)
  useEffect(
    () => () => {
      discoveryRequest.current?.cancel()
      viewportRequest.current?.cancel()
    },
    []
  )

  // Load markers from server based on viewport
  const loadMarkersInView = useCallback(async () => {
    if (!showAllMarkers || !accountId || extent.status === 'loading') return

    // Detect if filters changed since last fetch
    const filtersChanged = filters !== lastFetchedFiltersRef.current
    if (filtersChanged) lastFetchedFiltersRef.current = filters

    const firstFetch = isFirstFetch.current
    const isDiscovery = firstFetch || filtersChanged
    let box: MapBounds
    let grid: Grid

    if (isDiscovery) {
      // The discovery fetch needs no map: it runs as soon as the extent is
      // known, so the markers can decide the camera when the extent is null.
      const first = firstFetchBox(
        extent.status === 'loaded' ? extent.value : null
      )
      box = first.box
      grid = { zoom: first.zoom, cells: TILE_CELLS }
      if (firstFetch) isFirstFetch.current = false
    } else if (!map || !initialFitDone.current) {
      // Skip viewport-based fetches until the initial camera is applied,
      // otherwise idle events during init overwrite the discovery result
      return
    } else {
      const bounds = map.getBounds()
      if (!bounds) return
      const ne = bounds.getNorthEast()
      const sw = bounds.getSouthWest()
      box = {
        xmin: sw.lng(),
        ymin: sw.lat(),
        xmax: ne.lng(),
        ymax: ne.lat(),
      }
      grid = gridFor(
        Math.round(map.getZoom() || INITIAL_BOUNDS_ZOOM),
        metersOnMap.current
      )
    }

    if (isDiscovery) {
      discoveryRequest.current?.cancel()
      viewportRequest.current?.cancel()
      void cancelMarkerTiles(queryClient, accountId)
    } else {
      viewportRequest.current?.cancel()
    }
    const request = createLatestRequest(
      isDiscovery ? discoverySequence.current : viewportSequence.current
    )
    if (isDiscovery) discoveryRequest.current = request
    else viewportRequest.current = request

    // Alerts and meter details are never cached. They load next to the
    // clusters, and the map never waits for them: until they arrive, cells
    // show the last status this filter set had for them.
    if (isDiscovery) lastStatus.current = NO_STATUS
    const status = getClusteredMarkerStatus(
      accountId,
      isDiscovery ? box : coverBounds(box, grid.zoom, grid.cells),
      grid.zoom,
      filters,
      request.signal
    )
    status.catch(() => {})
    const show = (clusters: ClusterMarker[]) => {
      shownRequest.current = request
      setAllMarkers(
        mergeStatus(clusters, grid.zoom, lastStatus.current).map(
          transformClusterToSimpleMarker
        )
      )
    }

    let clusters: ClusterMarker[]
    try {
      clusters = isDiscovery
        ? await getClusteredMarkers(
            accountId,
            box,
            grid.zoom,
            filters,
            request.signal
          )
        : await fetchMarkersInView(queryClient, accountId, box, grid, filters)
      if (!request.isCurrent()) return
      show(clusters)

      // Only the discovery result feeds the camera decision. Viewport results
      // must not, or fitting them would fire another idle and fetch forever.
      if (isDiscovery) {
        metersOnMap.current = clusters.reduce((n, c) => n + c.pointCount, 0)
        setDiscovery({
          status: 'loaded',
          value: clusters.map(c => ({ lat: c.latitude, lng: c.longitude })),
        })
      }
    } catch (error) {
      if (!request.isCurrent()) return
      console.error('Error loading markers from API:', error)
      if (isDiscovery) {
        setAllMarkers([])
        setDiscovery({ status: 'failed' })
      }
      return
    }

    try {
      const fresh = await status
      // A late discovery status must not replace a newer viewport's markers.
      if (!request.isCurrent() || shownRequest.current !== request) return
      lastStatus.current = indexStatus(grid.zoom, fresh)
      show(clusters)
    } catch (error) {
      if (request.isCurrent()) console.error('Error loading marker status:', error)
    }
  }, [
    map,
    showAllMarkers,
    accountId,
    extent,
    transformClusterToSimpleMarker,
    filters,
    queryClient,
  ])

  // Discovery fetch: it runs on the extent, not on the map, so the camera can
  // be decided from the markers when the site has no extent.
  useEffect(() => {
    loadMarkersInView()
  }, [loadMarkersInView])

  // Single marker mode (when coordinates are provided)
  useEffect(() => {
    if (coordinates?.latitude && coordinates?.longitude) {
      setAllMarkers([
        {
          id: typeof singleMarkerData?.id === 'number' ? singleMarkerData.id : -9999,
          meterNumber: typeof singleMarkerData?.id === 'number' ? singleMarkerData.id : null,
          name: singleMarkerData?.name || '',
          address: singleMarkerData?.address || '',
          lastSeen: '',
          value: 0,
          position: {
            lat: coordinates.latitude,
            lng: coordinates.longitude,
          },
        },
      ])
    }
  }, [coordinates, singleMarkerData])

  // Listen to map idle event to reload markers when viewport changes
  useEffect(() => {
    if (!map || !isLoaded || !showAllMarkers) return

    // Add listener for viewport changes
    const listener = map.addListener('idle', loadMarkersInView)

    return () => {
      google.maps.event.removeListener(listener)
    }
  }, [map, isLoaded, showAllMarkers, loadMarkersInView])

  // Icons are as wide at every zoom, so the same markers need separating again
  // whenever the zoom changes, before any new markers arrive.
  const [mapZoom, setMapZoom] = useState<number | null>(null)
  useEffect(() => {
    if (!map) return
    const update = () =>
      setMapZoom(Math.round(map.getZoom() ?? INITIAL_BOUNDS_ZOOM))
    update()
    const listener = map.addListener('zoom_changed', update)
    return () => google.maps.event.removeListener(listener)
  }, [map])
  const serverCells = useMemo(
    () => allMarkers.some(m => m.pointCount !== undefined),
    [allMarkers]
  )
  const separatedMarkers = useMemo(
    () =>
      mapZoom !== null && serverCells
        ? separateClusters(allMarkers, mapZoom)
        : allMarkers,
    [allMarkers, mapZoom, serverCells]
  )

  // The fetched tiles reach far past the screen. Only markers in view, plus
  // half a screen around it for a short pan, go on the map. The separation
  // above still runs over all of them, so icons near the edge do not change
  // as the view moves.
  const [viewBox, setViewBox] = useState<MapBounds | null>(null)
  useEffect(() => {
    if (!map) return
    const update = () => {
      const bounds = map.getBounds()
      if (!bounds) return
      const ne = bounds.getNorthEast()
      const sw = bounds.getSouthWest()
      const next = {
        xmin: sw.lng(),
        ymin: sw.lat(),
        xmax: ne.lng(),
        ymax: ne.lat(),
      }
      setViewBox(prev =>
        prev &&
        prev.xmin === next.xmin &&
        prev.ymin === next.ymin &&
        prev.xmax === next.xmax &&
        prev.ymax === next.ymax
          ? prev
          : next
      )
    }
    update()
    const listener = map.addListener('idle', update)
    return () => google.maps.event.removeListener(listener)
  }, [map])
  const shownMarkers = useMemo(
    () =>
      viewBox && serverCells
        ? withinMargin(separatedMarkers, viewBox)
        : separatedMarkers,
    [separatedMarkers, serverCells, viewBox]
  )

  // Bypass viewport loader: feed full dataset to Supercluster for this test
  const [isAnimating, setIsAnimating] = useState(false)
  const polygonDraw = usePolygonDraw(
    map,
    vertices => onPolygonComplete?.(vertices),
    mapDiv => {
      // Frame the polygon in the part of the map left visible above the
      // page's overlay; the map itself can extend below the window.
      const rect = mapDiv.getBoundingClientRect()
      const overlayTop = window.innerHeight - (bottomOverlayHeight?.() ?? 0)
      const top = 70
      const bottom = Math.max(0, rect.bottom - overlayTop) + 24
      return {
        top,
        right: 40,
        bottom: Math.min(bottom, rect.height - top - 80),
        left: 40,
      }
    }
  )

  const [isFullMode, setIsFullMode] = useState(false)

  // Handle map load
  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      setMap(map)
      if (camera) {
        appliedCameraRef.current = JSON.stringify(camera)
        applyCamera(map, camera)
      }
      initialFitDone.current = true
    },
    [camera]
  )

  useEffect(() => {
    if (map) {
      const timer = setTimeout(() => {
        google.maps.event.trigger(map, 'resize')

        const hideAttribution = () => {
          const attributionElements = document.querySelectorAll(
            '.gmnoprint, .gm-style-cc, .gm-style-moc, .gm-style-pbc'
          )
          attributionElements.forEach(el => {
            if (el instanceof HTMLElement) {
              el.style.display = 'none'
            }
          })

          const googleLinks = document.querySelectorAll(
            'a[href*="maps.google.com"], a[href*="google.com/maps"]'
          )
          googleLinks.forEach(link => {
            if (link instanceof HTMLElement) {
              link.style.display = 'none'
            }
          })
        }
        hideAttribution()
        setTimeout(hideAttribution, 500)
        setTimeout(hideAttribution, 1000)
        setTimeout(hideAttribution, 2000)
      }, 200)

      return () => clearTimeout(timer)
    }
  }, [map])

  const checkIfFullMode = useCallback(() => {
    const widgetElement =
      document.querySelector('[class*="Widget"]') ||
      document.querySelector('[class*="widget"]') ||
      document.querySelector('[style*="100vh"]')

    if (widgetElement) {
      const rect = widgetElement.getBoundingClientRect()
      const isFull =
        rect.width >= window.innerWidth * 0.8 &&
        rect.height >= window.innerHeight * 0.8
      setIsFullMode(isFull)
    }
  }, [])

  useEffect(() => {
    checkIfFullMode()

    const handleResize = () => {
      checkIfFullMode()
    }

    window.addEventListener('resize', handleResize)

    // Check more frequently to catch widget expansion
    const interval = setInterval(checkIfFullMode, 500)

    return () => {
      window.removeEventListener('resize', handleResize)
      clearInterval(interval)
    }
  }, [checkIfFullMode])

  // Re-aim an already mounted map when the decision changes: another site, a
  // late extent, a filter change, or a different meter. The camera is derived
  // from the discovery result only, never from the viewport fetches, so this
  // cannot run idle → fetch → fitBounds → idle forever. Comparing by value
  // keeps a recomputed but unchanged camera from undoing the user's pan.
  useEffect(() => {
    if (!map || !isLoaded || !camera) return

    const key = JSON.stringify(camera)
    if (appliedCameraRef.current === key) return
    appliedCameraRef.current = key
    applyCamera(map, camera)
  }, [map, isLoaded, camera])

  const clearPolygon = () => {
    setSelected([])
    onPolygonClear?.()

    if (map) {
      const infoWindows = document.querySelectorAll('.gm-style-iw')
      infoWindows.forEach(infoWindow => {
        if (infoWindow instanceof HTMLElement) {
          infoWindow.style.display = 'none'
        }
      })
    }
  }

  // No camera means no place to open on. Say so instead of guessing a city.
  if (!isLoaded || !camera) {
    const message =
      view.kind === 'unavailable' ? UNAVAILABLE_COPY[view.reason] : null

    return (
      <Box
        role="status"
        aria-live="polite"
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          direction: 'rtl',
          backgroundColor: isDarkMode ? '#0F1E3D' : '#F5FAFF',
        }}
      >
        {!message && (
          <CircularProgress
            size={28}
            sx={{ color: isDarkMode ? '#60A5FA' : '#254D88' }}
          />
        )}
        <Typography
          sx={{
            fontFamily: 'Rubik, sans-serif',
            fontSize: 14,
            textAlign: 'center',
            px: 2,
            color: isDarkMode ? '#BBD7FF' : '#0B4F6C',
          }}
        >
          {message ?? LOADING_COPY}
        </Typography>
      </Box>
    )
  }

  return (
    <Box position="relative" height="100%" width="100%">
      {/* Hide Google Maps attribution */}
      <style>
        {`
          .gmnoprint, .gm-style-cc, .gm-style a[href^="https://maps.google.com/maps"] {
            display: none !important;
          }
          .gm-style-moc, .gm-style-pbc {
            display: none !important;
          }
          .gm-style a[href^="https://www.google.com/maps"] {
            display: none !important;
          }
        `}
      </style>
      {fullPage && canDraw && (
        <Box
          justifyContent="space-between"
          display="flex"
          position="absolute"
          top={20}
          right={40}
          zIndex={1100}
          gap={2}
          maxWidth={400}
        >
          <Box
            sx={{
              backgroundColor: uiColors.buttonBg,
              borderRadius: 1,
              width: 'fit-content',
              border: `1px solid ${polygonDraw.isDrawing ? uiColors.buttonBorderActive : uiColors.buttonBorder}`,
              paddingLeft: '10px',
              paddingRight: '10px',
              cursor: 'pointer',
              transition: 'background-color 0.3s ease, border-color 0.3s ease',
            }}
          >
            <img
              src={GroupIcon}
              alt="GroupIcon"
              style={{ width: '24px', height: '24px', marginTop: '6px', filter: uiColors.iconFilter }}
            />
          </Box>
          <Box
            sx={{
              backgroundColor: uiColors.buttonBg,
              borderRadius: 1,
              width: 'fit-content',
              border: `1px solid ${polygonDraw.isDrawing ? uiColors.buttonBorderActive : uiColors.buttonBorder}`,
              paddingLeft: '10px',
              paddingRight: '10px',
              cursor: 'pointer',
              transition: 'background-color 0.3s ease, border-color 0.3s ease',
            }}
            position="relative"
            onClick={() => {
              if (polygonDraw.isDrawing) {
                polygonDraw.cancel()
              } else {
                onDrawingStart?.()
                polygonDraw.start()
              }
            }}
          >
            <img
              src={PolylineIcon}
              alt="Polyline"
              style={{ width: '24px', height: '24px', marginTop: '6px', filter: uiColors.iconFilter }}
            />
            {polygon && (
              <IconButton
                aria-label="ניקוי בחירת אזור"
                onClick={event => {
                  event.stopPropagation()
                  clearPolygon()
                }}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  width: 18,
                  height: 18,
                  p: 0,
                  color: 'common.white',
                  backgroundColor: 'error.main',
                  '&:hover': { backgroundColor: 'error.dark' },
                }}
              >
                <Close sx={{ fontSize: 12 }} />
              </IconButton>
            )}
          </Box>
          <Autocomplete
            onLoad={onAutocompleteLoad}
            onPlaceChanged={onPlaceChanged}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              sx={{
                backgroundColor: uiColors.searchBg,
                borderRadius: '8px',
                padding: 1,
                width: '300px',
                border: `1px solid ${uiColors.searchBorder}`,
                transition: 'background-color 0.3s ease, border-color 0.3s ease',
              }}
            >
              <Search fontSize="small" sx={{ color: uiColors.searchText }} />
              <input
                type="text"
                placeholder="חיפוש"
                style={{
                  direction: 'rtl',
                  backgroundColor: 'transparent',
                  color: uiColors.searchText,
                  width: '100%',
                  border: 'none',
                  outline: 'none',
                  fontSize: '14px',
                  fontWeight: '400',
                  fontFamily: 'Roboto',
                  fontStyle: 'normal',
                }}
              />
            </Box>
          </Autocomplete>
        </Box>
      )}

      <GoogleMap
        mapContainerStyle={containerStyle}
        center={camera.center}
        zoom={camera.zoom}
        options={{
          ...mapOptions,
          clickableIcons: false,
          // draggable: interaction === 'interactive',
          // scrollwheel: interaction === 'interactive',
          // gestureHandling: interaction === 'interactive' ? 'greedy' : 'cooperative',
        }}
        onLoad={onMapLoad}
      >
        {polygon && !polygonDraw.isSketching && (
          <Polygon
            paths={verticesToPath(polygon)}
            options={SELECTED_POLYGON_OPTIONS}
          />
        )}
        {isLoaded && map && (
          <SuperClustering
            markers={shownMarkers}
            map={map}
            selectedMarkers={selected}
            markerIcon={createCustomMarkerIcon}
            clickable={!polygonDraw.isDrawing}
            onMarkerClick={(marker) => {
              debugLog('=== MARKER CLICKED ===')
              debugLog('Marker data:', JSON.stringify(marker, null, 2))
              setActiveMarker(marker)
            }}
            onClusterClick={cluster => {
              // Zoom in by 3 levels centered on the cluster
              const currentZoom = map?.getZoom() || 13
              const targetZoom = Math.min(currentZoom + 3, 18)

              setIsAnimating(true)

              map?.panTo(cluster.position)
              setTimeout(() => {
                map?.setZoom(targetZoom)
                setTimeout(() => {
                  setIsAnimating(false)
                }, 1000)
              }, 200)
            }}
            clusterDistance={40}
            minZoom={5}
            maxZoom={17}
            maxZoomClustering={16} // Keep clustering until you're closer
            isAnimating={isAnimating}
          />
        )}

        {/* Render info card for active marker */}
        {activeMarker && (
          <MarkerInfoCard
            position={activeMarker.position}
            meterId={activeMarker.meterNumber ? String(activeMarker.meterNumber) : ''}
            address={activeMarker.address}
            name={activeMarker.name}
            lastSeen={activeMarker.lastSeen || ''}
            onClose={() => setActiveMarker(null)}
            isDarkMode={isDarkMode}
          />
        )}

      </GoogleMap>

      {isFullMode && (
        <Slide direction="up" in={selected.length > 0} timeout={400}>
          <Paper
            elevation={8}
            sx={{
              position: 'absolute',
              bottom: 20,
              left: 20,
              p: 2,
              borderRadius: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              maxWidth: 300,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
              },
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <DragIndicator color="primary" />
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                sx={{ fontFamily: 'Rubik, sans-serif' }}
              >
                Selected Markers ({selected.length})
              </Typography>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              {selected.map((id, index) => {
                const marker = allMarkers.find(m => m.id === id)
                return (
                  <Grow key={id} in timeout={200 + index * 100}>
                    <Chip
                      label={marker?.name || `Marker ${id}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'scale(1.05)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        },
                      }}
                    />
                  </Grow>
                )
              })}
            </Box>
          </Paper>
        </Slide>
      )}
    </Box>
  )
}
